import { type PortableTextBlock, stegaClean } from 'next-sanity'

import { buttonSizeClasses } from '@/app/components/Button'
import { Media } from '@/app/components/Media'
import PortableText from '@/app/components/PortableText'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { getBottomSpacingClass, getTopSpacingClass } from '@/app/utils/sectionSpacing'
import { cn, ptFontNormalHeaders } from '@/app/utils/style'
import type { SideBySideSection as SideBySideSectionProps } from '@/sanity.types'

type SideBySideComponentProps = {
  block: SideBySideSectionProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
}

export default function SideBySideSection({
  block,
  index,
  pageId,
  pageType,
}: SideBySideComponentProps) {
  if (!block?.items?.length) return null

  const isLCP = index < 2
  const topSpacingClass = getTopSpacingClass(stegaClean(block.topSpacing))
  const bottomSpacingClass = getBottomSpacingClass(stegaClean(block.bottomSpacing))

  return (
    <div className={cn('content-container', topSpacingClass, bottomSpacingClass)}>
      <div className="mx-auto flex max-w-[557px] flex-col gap-11xl md:max-w-[1144px] lg:gap-14xl">
        {block.items.map((item, idx) => {
          if (!item?.content || !item?.image) return null

          const imagePosition = stegaClean(item.imagePosition) || 'right'
          const isImageRight = imagePosition === 'right'
          const centerOnMobile = stegaClean(item.centerContentOnMobile) ?? false
          // Prioritize first image in first section for LCP optimization
          const isPriorityImage = isLCP && idx === 0

          return (
            <div
              key={item._key || idx}
              className={cn(
                'flex flex-col gap-4xl',
                'md:flex-row md:items-center md:gap-6xl lg:gap-10xl',
                isImageRight && 'md:flex-row-reverse',
              )}
            >
              <div className="flex-1">
                <Media
                  image={item.image}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 420px"
                  {...(isPriorityImage
                    ? { priority: true, fetchPriority: 'high' as const }
                    : { loading: 'lazy' as const, lazyLoadMargin: 300 })}
                />
              </div>
              <div className={cn('flex-1', centerOnMobile && 'text-center md:text-left')}>
                <PortableText
                  className={cn(
                    ptFontNormalHeaders,
                    '[&_h4]:font-medium',
                    '[&_p[class~="lg:size-header-h4"]]:font-medium',
                    '[&_[class*=size-header]_mark]:font-medium',
                    centerOnMobile && 'md:[&_*]:text-left',
                  )}
                  tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                  buttonClassName={buttonSizeClasses({ lg: 'md' })}
                  paragraphClassName="size-body-xs lg:size-body-sm"
                  headingConfig={{
                    h2: { default: 'h4', md: 'h4', lg: 'h2' },
                  }}
                  value={item.content as PortableTextBlock[]}
                  documentId={pageId}
                  documentType={pageType}
                  parentPath={
                    block._key && item._key
                      ? `pageBuilder[_key=="${block._key}"].items[_key=="${item._key}"].content`
                      : undefined
                  }
                  headingsAsParagraphs={stegaClean(block.headingsAsParagraphs) ?? false}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
